import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Modal, notification } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { getResponseErrorMessage } from '@/utils/api-service';

import api from '@/api/index';

import './styles/voiceover-reorder.scss';

interface VoiceoverItem {
  video_id: number;
  content_source_id?: number;
  name: string;
  sort_order: number | null;
}

interface Props {
  voiceovers: VoiceoverItem[];
  visible: boolean;
  mode?: 'video' | 'season';
  movieId?: number | string;
  serialId?: number | string;
  seasonId?: number | string;
  episodeId?: number | string;
  onClose: () => void;
  onSaved: () => void;
}

function SortableItem({ id, item, index }: { id: number; item: VoiceoverItem; index: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="voiceover-reorder__item">
      <button className="voiceover-reorder__handle" {...attributes} {...listeners}>
        <HolderOutlined />
      </button>
      <span className="voiceover-reorder__index">{index + 1}.</span>
      <span className="voiceover-reorder__name">{item.name}</span>
    </div>
  );
}

function VoiceoverReorder({ voiceovers, visible, mode = 'video', movieId, serialId, seasonId, episodeId, onClose, onSaved }: Props) {
  const [items, setItems] = useState<VoiceoverItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const isSeasonMode = mode === 'season';

  const getItemId = (item: VoiceoverItem) => isSeasonMode ? (item.content_source_id ?? item.video_id) : item.video_id;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (visible) {
      const sorted = [...voiceovers].sort((a, b) => {
        const aOrder = a.sort_order;
        const bOrder = b.sort_order;
        if (aOrder !== null && bOrder !== null) return aOrder - bOrder;
        if (aOrder !== null) return -1;
        if (bOrder !== null) return 1;
        return 0;
      });
      setItems(sorted);
    }
  }, [visible, voiceovers]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => getItemId(item) === active.id);
        const newIndex = items.findIndex((item) => getItemId(item) === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (isSeasonMode && serialId && seasonId) {
        const order = items.map((item, index) => ({
          content_source_id: item.content_source_id ?? item.video_id,
          sort_order: index,
        }));
        await api.season.updateVoiceoversOrder(serialId, seasonId, order);
      } else if (!isSeasonMode) {
        const order = items.map((item, index) => ({
          video_id: item.video_id,
          sort_order: index,
        }));
        if (movieId) {
          await api.movie.updateVoiceoversOrder(movieId, order);
        } else if (serialId && seasonId && episodeId) {
          await api.serie.updateVoiceoversOrder(serialId, seasonId, episodeId, order);
        } else {
          throw new Error('Не указан идентификатор для сохранения порядка');
        }
      } else {
        throw new Error('Не указан сезон для сохранения порядка озвучек');
      }

      notification.success({ message: 'Порядок озвучек сохранён' });
      onSaved();
      onClose();
    } catch (err: any) {
      notification.error({
        message: getResponseErrorMessage(err, 'Ошибка при сохранении порядка'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsSaving(true);
      if (isSeasonMode && serialId && seasonId) {
        await api.season.resetVoiceoversOrder(serialId, seasonId);
      } else if (movieId) {
        await api.movie.resetVoiceoversOrder(movieId);
      }
      notification.success({ message: 'Порядок сброшен' });
      onSaved();
      onClose();
    } catch (err: any) {
      notification.error({
        message: getResponseErrorMessage(err, 'Ошибка при сбросе порядка'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Порядок озвучек"
      open={visible}
      onCancel={onClose}
      width={560}
      footer={
        <div className="voiceover-reorder__footer">
          {(movieId || (isSeasonMode && serialId && seasonId)) && (
            <button className="app-btn secondary" onClick={handleReset} disabled={isSaving}>
              Сбросить
            </button>
          )}
          <button className="app-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      }
    >
      {items.length === 0 ? (
        <p style={{ color: '#999', textAlign: 'center', padding: '2rem 0' }}>
          Нет озвучек для сортировки
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((item) => getItemId(item))}>
            <div className="voiceover-reorder__list">
              {items.map((item, index) => (
                <SortableItem key={getItemId(item)} id={getItemId(item)} item={item} index={index} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <p className="voiceover-reorder__hint">
        Перетащите озвучки в нужном порядке и нажмите «Сохранить».
      </p>
    </Modal>
  );
}

export default VoiceoverReorder;
